// Simple three.js example

import * as THREE from 'three/build/three.min';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Wireframe } from 'three/examples/jsm/lines/Wireframe.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { BufferGeometryUtils } from 'three/examples/jsm/utils/BufferGeometryUtils';
import barba from '@barba/core';
import { getWeatherColor } from '../utils/color/color';
import { gsap } from 'gsap';
import SimplexNoise from 'simplex-noise';

const simplex = new SimplexNoise();

let wireframe, renderer, scene, camera, controls;
let matLine, materialLogo;

let blob, canvas;
let showLogo = false;
let showLogoState = false;
let mouse = new THREE.Vector2( 0.8, 0.5 );
let width, height;
const debug = false;
const startPos = { x: 0, y: 0, z: 0 };
let cameraMinPos;
let cameraMaxPos;
const scrollThreshold = 80; //px
let lastKnownScrollPosition = 0;
let ticking = false;
let boxSize;
let isInitialized = false;
let visibleHeight;
let visibleWidth;
let viewportIsPortrait;
const selector = '#scene';
window.addEventListener( 'mousemove', onMouseMove );
document.addEventListener( 'DOMContentLoaded', () => {
  setSizes();
  init();
} );


function setSizes() {
  width = window.innerHeight;
  height = window.innerWidth;
  viewportIsPortrait = width >= height;

  if ( boxSize ) {
    cameraMinPos = { x: 0, y: 0, z: 500 };
    cameraMaxPos = {
      x: -boxSize * 1.1,
      y: ( boxSize * 0.1 ),
      z: 200,
    };
  }

  setShowLogo();
}

function setShowLogo() {
  const showOnThisPage = document.body.classList.contains( 'home' );
  showLogo = lastKnownScrollPosition <= scrollThreshold && showOnThisPage;
}

async function init() {

  let color = await getWeatherColor( debug );
  document.documentElement.style.setProperty( '--base-root', `${ color }` );// set color to css vars

  canvas = document.querySelector( selector );
  renderer = new THREE.WebGLRenderer( { canvas: canvas, antialias: true } );
  renderer.setPixelRatio( window.devicePixelRatio );
  renderer.setClearColor( color );
  renderer.setSize( window.innerWidth, window.innerHeight );
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap; // default THREE.PCFShadowMap
  document.body.appendChild( renderer.domElement );

  scene = new THREE.Scene();

  camera = new THREE.PerspectiveCamera( 40, window.innerWidth / window.innerHeight, 1, 1000 );

  boxSize = getBoxSize();
  setSizes();
  const cameraStartPos = showLogo ? cameraMinPos : cameraMaxPos;
  camera.position.set( cameraStartPos.x, cameraStartPos.y, cameraStartPos.z );

  const group = new THREE.Group();
  scene.add( group );
  // controls = new OrbitControls( group, renderer.domElement );
  controls = new OrbitControls( group, document.body );
  controls.target.set( 0, 0, 1 );
  controls.rotateSpeed = 4; // mouse sensitivity

  ////////////////////////////
  // LIGHT
  ////////////////////////////

  let light = new THREE.HemisphereLight( color, color, 0.2 );
  scene.add( light );
  let light2 = new THREE.DirectionalLight( color, 0.5 );
  light.position.set( 200, 300, 400 );
  scene.add( light2 );
  let light3 = light2.clone();
  light3.position.set( -200, 300, 400 );
  scene.add( light3 );


  ////////////////////////////
  // BOX
  ////////////////////////////
  // const boxSize = 150;
  let boxGeometry = new THREE.BoxBufferGeometry( boxSize, boxSize, boxSize );
  let edgesGeometry = new THREE.EdgesGeometry( boxGeometry );

  let lineGeometry = new LineSegmentsGeometry().fromEdgesGeometry( edgesGeometry );

  matLine = new LineMaterial( {
    color: 0x000000,
    linewidth: Math.floor( boxSize / 30 ),
    transparent: true,
    opacity: 0,
  } );

  wireframe = new Wireframe( lineGeometry, matLine );
  wireframe.computeLineDistances();
  wireframe.scale.set( 1, 1, 1 );
  wireframe.position.set( startPos.x, startPos.y, startPos.z );
  wireframe.rotation.setFromVector3( new THREE.Vector3( THREE.Math.degToRad( -3 ), THREE.Math.degToRad( 3 ), 0 ) );

  group.add( wireframe );

  ////////////////////////////
  // BLOB
  ////////////////////////////

  let blobGeometry = new THREE.IcosahedronGeometry( boxSize / 2, 20 );
  blobGeometry = BufferGeometryUtils.mergeVertices( blobGeometry );
  blobGeometry.setAttribute( 'basePosition', new THREE.BufferAttribute().copy( blobGeometry.attributes.position ) );
  let blobMaterial = new THREE.MeshPhongMaterial( {
    emissive: color,
    emissiveIntensity: 0.5,
    shininess: 0,
  } );
  blob = new THREE.Mesh( blobGeometry, blobMaterial );
  blob.castShadow = true;
  blob.receiveShadow = true;
  blob.position.set( startPos.x, startPos.y, startPos.z );
  group.add( blob );


  // group.rotation.set(0,0,-200);

  ////////////////////////////
  // END BLOB
  ////////////////////////////
  const width = boxSize * 0.7;
  const height = width;
  const geometry = new THREE.PlaneGeometry( width, height );
  const posZ = 150;

  const url = `${ phpVars.templateUrl }/public/images/logo.png`;
  let texture = window.innerWidth < 1024 ? loadTexture( url ) : loadTextureAndMakePowerOf2( url );
  // let texture = loadTexture( url );

  // texture.anisotropy = renderer.getMaxAnisotropy();
  texture.anisotropy = renderer.capabilities.getMaxAnisotropy()

  //Filters
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipMapLinearFilter;

  materialLogo = new THREE.MeshStandardMaterial( {
    map: texture,
    transparent: true,
    side: THREE.FrontSide,
    opacity: 0,
  } );

  const object = new THREE.Mesh( geometry, materialLogo );
  object.castShadow = true;
  object.receiveShadow = true;
  object.position.set(
    object.position.z,
    object.position.y,
    posZ,
  );

  scene.add( object );

  controls.update();
  isInitialized = true;
  window.addEventListener( 'resize', onWindowResize, false );
  onWindowResize();
  requestAnimationFrame( render );

}

function setNewPoints( a ) {
  const basePositionAttribute = blob.geometry.getAttribute( 'basePosition' );
  const positionAttribute = blob.geometry.getAttribute( 'position' );
  const vertex = new THREE.Vector3();

  for ( let vertexIndex = 0; vertexIndex < positionAttribute.count; vertexIndex++ ) {
    vertex.fromBufferAttribute( basePositionAttribute, vertexIndex );

    const amplitude = showLogo ? 0.0001 : 0.00002;
    // const amplitude = showLogo ? 0.0002 : 0.00005;
    let perlin = simplex.noise3D(
      vertex.x * 0.006 + a * amplitude,
      vertex.y * 0.006 + a * amplitude,
      vertex.z * 0.006,
    );

    const base = 0.1;
    const size = 1.3;
    let ratio = perlin * 2 * ( ( mouse.y * .5 ) + base ) + size;

    vertex.multiplyScalar( ratio );
    positionAttribute.setXYZ( vertexIndex, vertex.x, vertex.y, vertex.z );
  }
  blob.geometry.attributes.position.needsUpdate = true;
  blob.geometry.computeVertexNormals();
  blob.geometry.computeBoundingSphere();

}

function onMouseMove( e ) {
  gsap.to( mouse, {
    y: ( e.clientY / height ).toFixed( 2 ),
    x: ( e.clientX / width ).toFixed( 2 ),
    duration: 0.2,
    ease: 'circ. out',
  } );
}

function onWindowResize() {

  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();

  renderer.setSize( window.innerWidth, window.innerHeight );
  setSizes();
  sketchToggleLogo();
}

let clock = new THREE.Clock();
let delta = 0;
// fps
let interval = 1 / 24;


function render( a ) {
  requestAnimationFrame( render );
  delta += clock.getDelta();
  if ( delta > interval ) {
    setNewPoints( a );
    matLine.resolution.set( window.innerWidth, window.innerHeight ); // resolution of the viewport
    controls.update();
    renderer.render( scene, camera );
    delta = delta % interval;
  }
}


function sketchToggleLogo() {
  const duration = 1.5;

  setShowLogo();
  if ( showLogo !== showLogoState ) {
    showLogoState = ! showLogoState;
    const opacity = showLogo ? 1 : 0;

    gsap.to( matLine, { opacity: opacity, duration: duration, ease: 'circ. out' } );
    gsap.to( materialLogo, { opacity: opacity, duration: duration, ease: 'circ. out' } );

    const newCameraPos = showLogo ? cameraMinPos : cameraMaxPos;
    gsap.to( camera.position, {
      x: newCameraPos.x,
      y: newCameraPos.y,
      z: newCameraPos.z,
      duration: duration,
      ease: 'circ. out',
    } );

    if ( showLogo ) {
      document.querySelector( selector ).classList.remove( 'pointer-events-none' );
    } else {
      document.querySelector( selector ).classList.add( 'pointer-events-none' );

    }
  }
}

window.onload = ( event ) => {
  document.body.style.touchAction = null;
  if ( isInitialized )
    sketchToggleLogo( barba.history.current.scroll.y );
};
barba.hooks.afterEnter( ( data ) => {
  document.body.style.touchAction = null;
  if ( isInitialized )
    sketchToggleLogo( barba.history.current.scroll.y );
} );
// On Scroll
// Reference: http://www.html5rocks.com/en/tutorials/speed/animations/

document.addEventListener( 'scroll', function ( e ) {
  lastKnownScrollPosition = window.scrollY;

  if ( ! ticking ) {
    window.requestAnimationFrame( function () {
      sketchToggleLogo( lastKnownScrollPosition );
      ticking = false;
    } );

    ticking = true;
  }
} );
// END On Scroll


const visibleHeightAtZDepth = ( depth, camera ) => {
  // compensate for cameras not positioned at z=0
  const cameraOffset = camera.position.z;
  if ( depth < cameraOffset ) depth -= cameraOffset;
  else depth += cameraOffset;

  // vertical fov in radians
  const vFOV = camera.fov * Math.PI / 180;

  // Math.abs to ensure the result is always positive
  return 2 * Math.tan( vFOV / 2 ) * Math.abs( depth );
};

const visibleWidthAtZDepth = ( depth, camera ) => {
  const height = visibleHeightAtZDepth( depth, camera );
  return height * camera.aspect;
};

const getBoxSize = () => {
  visibleHeight = visibleHeightAtZDepth( 1000, camera );
  visibleWidth = visibleWidthAtZDepth( 1000, camera );
  const divideBy = viewportIsPortrait ? 4 : 5;
  return visibleHeight <= visibleWidth ? visibleHeight / divideBy : visibleWidth / divideBy;
};

function nextPowerOf2( x ) {
  return Math.pow( 2, Math.ceil( Math.log( x ) / Math.log( 2 ) ) );
}

function loadTextureAndMakePowerOf2( url ) {

  var image = new Image();
  var texture = new THREE.Texture( undefined );

  image.onload = function () {

    // round to nearest power of 2
    const nextWidth = nextPowerOf2( image.width );
    const nextHeight = nextPowerOf2( image.height );
    if ( nextWidth != image.width || nextHeigh != image.height ) {
      var c = document.createElement( 'canvas' );
      c.width = nextWidth;
      c.height = nextHeight;
      var ctx = c.getContext( '2d' );
      ctx.drawImage( image, 0, 0, nextWidth, nextHeight );
      // console.log("scaled image from " + image.width + "x" + image.height +
      //   " to " + nextWidth + "x" + nextHeight);
      texture.image = c;
    }
    texture.needsUpdate = true;
  };

  texture.image = image;
  texture.sourceFile = url;
  image.src = url;

  return texture;

}

function loadTexture( url ) {
  // const texture = new THREE.TextureLoader().load( url );
  // texture.anisotropy = 0;
  // texture.magFilter = THREE.NearestFilter;
  // texture.minFilter = THREE.NearestFilter;
  const texture = new THREE.TextureLoader().load( url );
  return texture;
}
